import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import isWorkflowRunNotFoundFailure from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/Helpers/isWorkflowRunNotFoundFailure';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Result = {
	readonly preparationId: number;
	/** `null` until the preparation is loaded, open and this capture's own. */
	readonly preparation: WorkflowRunView | null;
	readonly preparationPath: string;
};

/**
 * The preparation a capture screen is opened for, read from `?preparation=`. Capture is only ever
 * reached from a preparation: a missing or malformed id, a preparation abandoned in the meantime
 * (`404`) or one that no longer receives elements all leave the screen — to the dossier, or back to
 * the preparation when it still exists.
 */
const useCapturePreparation = (subject: SubjectAddress): Result => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isNotFound, setIsNotFound] = React.useState(false);

	const rawPreparationId = searchParams.get('preparation');
	const preparationId = rawPreparationId !== null ? Number(rawPreparationId) : Number.NaN;
	const isInvalidPreparationId = !Number.isInteger(preparationId) || preparationId <= 0;
	const dossierPath = buildSubjectPath(subject);
	const preparationPath = `${dossierPath}/preparations/${preparationId}`;

	const { data: preparation } = useStoreQuery({
		selector: (state) => state.workflowRunsStore.workflowRun,
		stopOn: (value) => value !== null && value.id === preparationId,
		query: () => {
			if (isInvalidPreparationId) {
				return Promise.resolve();
			}

			return dispatch(fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, preparationId))).catch(
				(rejection: unknown) => {
					if (isWorkflowRunNotFoundFailure(rejection)) {
						setIsNotFound(true);
					}

					throw rejection;
				},
			);
		},
		deps: [facilityId, subject, preparationId],
	});

	const current = preparation !== null && preparation.id === preparationId ? preparation : null;
	const isClosed = current !== null && !current.isOpen;

	React.useEffect(() => {
		if (isInvalidPreparationId || isNotFound) {
			navigate(dossierPath, { replace: true });
			return;
		}

		if (isClosed) {
			navigate(preparationPath, { replace: true });
		}
	}, [isInvalidPreparationId, isNotFound, isClosed, navigate, dossierPath, preparationPath]);

	return {
		preparationId,
		preparation: isClosed ? null : current,
		preparationPath,
	};
};

export default useCapturePreparation;
